/**
 * Memories API functions
 * For managing event/album-style memories that group people and media
 */

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000/api";

// =============================================================================
// Types
// =============================================================================

export interface MemorySummary {
  id: number;
  title: string;
  description: string | null;
  memory_date: string | null;
  location_name: string | null;
  cover_media_id: number | null;
  media_count: number;
  person_count: number;
}

export interface TaggedMediaInfo {
  id: number;
  filename: string;
  original_filename: string;
  content_type: string;
  caption: string | null;
}

export interface TaggedPersonInfo {
  id: number;
  display_name: string;
  tree_id: number | null;
}

export interface TaggedConnectedPersonInfo {
  id: number;
  display_name: string;
}

export interface LocationInfo {
  id: number;
  name: string;
  city: string | null;
  country: string | null;
}

export interface MemoryDetail {
  id: number;
  title: string;
  description: string | null;
  memory_date: string | null;
  memory_date_parsed: string | null;
  location: LocationInfo | null;
  cover_media_id: number | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  media_items: TaggedMediaInfo[];
  persons: TaggedPersonInfo[];
  connected_persons: TaggedConnectedPersonInfo[];
}

export interface MemoryCreate {
  title: string;
  description?: string;
  memory_date?: string;
  memory_date_parsed?: string;
  location_id?: number;
  cover_media_id?: number;
  person_ids?: number[];
  connected_person_ids?: number[];
}

export interface MemoryUpdate {
  title?: string;
  description?: string;
  memory_date?: string;
  memory_date_parsed?: string;
  location_id?: number | null;
  cover_media_id?: number | null;
}

export interface TagRequest {
  media_id?: number;
  person_id?: number;
  connected_person_id?: number;
}

// =============================================================================
// CRUD Operations
// =============================================================================

/**
 * List all memories with optional filtering
 */
export async function listMemories(
  token: string,
  options?: {
    search?: string;
    limit?: number;
    offset?: number;
  }
): Promise<MemorySummary[]> {
  const params = new URLSearchParams();
  if (options?.search) params.append("search", options.search);
  if (options?.limit) params.append("limit", options.limit.toString());
  if (options?.offset) params.append("offset", options.offset.toString());

  const queryString = params.toString();
  const url = `${API_URL}/memories${queryString ? `?${queryString}` : ""}`;

  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch memories: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get a specific memory with all tagged items
 */
export async function getMemory(
  token: string,
  memoryId: number
): Promise<MemoryDetail> {
  const response = await fetch(`${API_URL}/memories/${memoryId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch memory: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Create a new memory
 */
export async function createMemory(
  token: string,
  data: MemoryCreate
): Promise<MemorySummary> {
  const response = await fetch(`${API_URL}/memories`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || `Failed to create memory: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Update a memory
 */
export async function updateMemory(
  token: string,
  memoryId: number,
  data: MemoryUpdate
): Promise<MemorySummary> {
  const response = await fetch(`${API_URL}/memories/${memoryId}`, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || `Failed to update memory: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Delete a memory
 */
export async function deleteMemory(
  token: string,
  memoryId: number
): Promise<void> {
  const response = await fetch(`${API_URL}/memories/${memoryId}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || `Failed to delete memory: ${response.statusText}`);
  }
}

// =============================================================================
// Tagging Operations
// =============================================================================

/**
 * Tag a media, person, or connected person to a memory
 */
export async function tagItemToMemory(
  token: string,
  memoryId: number,
  tagData: TagRequest
): Promise<{ message: string }> {
  const response = await fetch(`${API_URL}/memories/${memoryId}/tag`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(tagData),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || `Failed to tag item: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Remove a tag from a memory
 */
export async function untagItemFromMemory(
  token: string,
  memoryId: number,
  tagData: TagRequest
): Promise<void> {
  const params = new URLSearchParams();
  if (tagData.media_id) params.append("media_id", tagData.media_id.toString());
  if (tagData.person_id) params.append("person_id", tagData.person_id.toString());
  if (tagData.connected_person_id) {
    params.append("connected_person_id", tagData.connected_person_id.toString());
  }

  const response = await fetch(
    `${API_URL}/memories/${memoryId}/tag?${params.toString()}`,
    {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || `Failed to untag item: ${response.statusText}`);
  }
}

// =============================================================================
// Lookup Operations
// =============================================================================

/**
 * Get memories tagged to a specific media item
 */
export async function getMemoriesForMedia(
  token: string,
  mediaId: number
): Promise<MemorySummary[]> {
  const response = await fetch(`${API_URL}/memories/by-media/${mediaId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch memories for media: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get memories tagged to a specific person
 */
export async function getMemoriesForPerson(
  token: string,
  personId: number
): Promise<MemorySummary[]> {
  const response = await fetch(`${API_URL}/memories/by-person/${personId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch memories for person: ${response.statusText}`);
  }

  return response.json();
}
